/**
 * utils/riskValidation.ts
 * Validation and formatting utilities for risk configuration
 */

// Internal absolute imports
import { formatPercentage } from '@/utils/numberHelpers';
import { formatMoney } from '@/utils/moneyHelpers';
import type { RiskSpec } from '@/types/schema/AccountRisk';

/**
 * Validate percentage risk value
 * Must be between 0.0001 (0.01%) and 1.0 (100%)
 *
 * @param value - Decimal value to validate
 * @returns true if valid, false otherwise
 */
export function validatePercentageRisk(value: number): boolean {
  return Number.isFinite(value) && value >= 0.0001 && value <= 1.0;
}

/**
 * Validate fixed dollar risk value
 * Must be positive integer (in cents)
 *
 * @param value - Integer value in cents to validate
 * @returns true if valid, false otherwise
 */
export function validateFixedRisk(value: number): boolean {
  return Number.isInteger(value) && value > 0;
}

/**
 * Validate profile name
 * Must be non-empty and unique within existing profiles
 *
 * @param name - Profile name to validate
 * @param existingProfiles - Array of existing profiles
 * @param currentProfileId - ID of profile being edited (to exclude from uniqueness check)
 * @returns true if valid, false otherwise
 */
export function validateProfileName(
  name: string,
  existingProfiles: Array<{ tempId: string; name: string }>,
  currentProfileId?: string
): boolean {
  if (!name || !name.trim()) {
    return false;
  }

  // Check uniqueness (case-insensitive)
  const normalizedName = name.trim().toLowerCase();
  return !existingProfiles.some(
    profile =>
      profile.tempId !== currentProfileId &&
      profile.name.toLowerCase() === normalizedName
  );
}

/**
 * Format risk specification for display
 *
 * @param risk - Risk specification to format
 * @param currency - Currency for fixed dollar amounts (default: 'USD')
 * @returns Formatted string for display
 *
 * @example
 * formatRiskDisplay({ type: 'percentage', value: 0.015 }) // Returns: "1.50%"
 * formatRiskDisplay({ type: 'fixed', value: 10000 }) // Returns: "$100.00"
 */
export function formatRiskDisplay(risk: RiskSpec, currency: string = 'USD'): string {
  if (risk.type === 'percentage') {
    return formatPercentage(risk.value, 2);
  } else {
    // Fixed risk is stored as integer in cents, precision 2
    return formatMoney(risk.value, currency, 2);
  }
}

/**
 * Get error message for invalid percentage risk
 *
 * @param value - Value that failed validation
 * @returns Human-readable error message
 */
export function getPercentageRiskError(value: number): string {
  if (!Number.isFinite(value)) {
    return 'Please enter a valid number';
  }
  if (value < 0.0001) {
    return 'Risk must be at least 0.01%';
  }
  if (value > 1.0) {
    return 'Risk cannot exceed 100%';
  }
  return 'Invalid percentage risk value';
}

/**
 * Get error message for invalid fixed risk
 *
 * @param value - Value that failed validation
 * @returns Human-readable error message
 */
export function getFixedRiskError(value: number): string {
  if (!Number.isFinite(value)) {
    return 'Please enter a valid amount';
  }
  if (!Number.isInteger(value)) {
    return 'Amount must not have more than 2 decimal places';
  }
  if (value <= 0) {
    return 'Risk must be greater than zero';
  }
  return 'Invalid fixed risk amount';
}

/**
 * Get error message for invalid profile name
 *
 * @param name - Name that failed validation
 * @param existingProfiles - Array of existing profiles for uniqueness check
 * @returns Human-readable error message
 */
export function getProfileNameError(
  name: string,
  existingProfiles: Array<{ tempId: string; name: string }>
): string {
  if (!name || !name.trim()) {
    return 'Profile name is required';
  }

  const normalizedName = name.trim().toLowerCase();
  const isDuplicate = existingProfiles.some(
    profile => profile.name.toLowerCase() === normalizedName
  );

  if (isDuplicate) {
    return 'A profile with this name already exists';
  }

  return 'Invalid profile name';
}
